import React from 'react';
import { useDispatch } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { Spacer, ToastProvider } from '@zillow/constellation-10';

import {
  EnterprisePageFeature,
  PermissionCheck,
  RentalManagerEnterpriseEventTrackingWebApiHandler_trackPageVisit,
  USER_MANAGEMENT_PAGE,
  useRentalManagerUser,
  useRmwPaths,
} from '@zg-rentals/rental-manager-env';
import { useRentalManagerUserManagementCompanyWebApiHandler_getUserCompanyStats } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { COMPANY_STATS_TAG } from '../UsersPage';
import ActivityHistoryPage from '../ActivityHistoryPage';
import ApplicationPermissionsPage from '../ApplicationPermissionsPage';
import UnrecognizedUsersPage from '../UnrecognizedUsersPage';
import UsersPage from '../UsersPage';
import UserManagementNav from './UserManagementNav';
import { useActiveTab } from '../../hooks/useActiveTab';
import UserManagementActions from '../../redux/actions/UserManagementActions';
import { useApplicationPermissions } from '../../hooks/useApplicationPermissions';
import { PageWrapper } from '@zg-rentals/rental-revenue-components';
import { useActiveUsers } from '../../hooks/useActiveUsers';
import { usePendingUsers } from '../../hooks/usePendingUsers';

const DEFAULT_PAGE_TITLE = 'User management';

const UserManagementPage = () => {
  const paths = useRmwPaths();
  const dispatch = useDispatch();
  const activeTab = useActiveTab();
  const activeUserState = useActiveUsers();
  const pendingUserState = usePendingUsers();
  const { data: company } = useRentalManagerUserManagementCompanyWebApiHandler_getUserCompanyStats({
    meta: { tags: [COMPANY_STATS_TAG], context: { api: 'rental-manager-api', details: 'getUserCompanyStats' } },
  });
  const { getUsersWithApplicationPermissionDetails } = useApplicationPermissions();
  const { user } = useRentalManagerUser();

  React.useEffect(() => {
    getUsersWithApplicationPermissionDetails();
    if (user.customers.length && !user.impersonatorEmail && !user.impersonatorUserToken) {
      RentalManagerEnterpriseEventTrackingWebApiHandler_trackPageVisit({
        page: EnterprisePageFeature.USER_MANAGEMENT,
      }).catch(() => {});
    }
    return () => {
      dispatch(UserManagementActions.clearUserManagementProfiles());
    };
  }, []);

  const getSectionUrl = (section: string) => {
    const baseDetailsUrl = paths.userManagementPage;
    return `${baseDetailsUrl}/${section}([?].*)?`;
  };

  const renderChildRoutes = () => {
    // child paths
    const childPathUsers = getSectionUrl(paths.userManagementUsers);
    const childPathPending = getSectionUrl(paths.userManagementPending);
    const childPathApplicationPermissions = getSectionUrl(paths.userManagementApplicationPermissions);
    const childPathActivity = getSectionUrl(paths.userManagementActivity);
    const childPathUnrecognized = getSectionUrl(paths.userManagementUnrecognizedUsers);
    return (
      <Switch>
        <Route path={childPathUsers}>
          <UsersPage userState={activeUserState} />
        </Route>
        <Route path={childPathPending}>
          <UsersPage userState={pendingUserState} />
        </Route>
        <Route component={ApplicationPermissionsPage} path={childPathApplicationPermissions} />
        <Route component={ActivityHistoryPage} path={childPathActivity} />
        <Route component={UnrecognizedUsersPage} path={childPathUnrecognized} />
      </Switch>
    );
  };

  return (
    <ToastProvider>
      <PermissionCheck route={USER_MANAGEMENT_PAGE}>
        <PageWrapper title={DEFAULT_PAGE_TITLE}>
          <Spacer css={{ padding: 'sm' }}>
            <div className="UserManagementPage-card">
              <UserManagementNav company={company} activeTab={activeTab} />
              {renderChildRoutes()}
            </div>
          </Spacer>
        </PageWrapper>
      </PermissionCheck>
    </ToastProvider>
  );
};

export default UserManagementPage;
